import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Instagram, Youtube, Globe, Link2, ExternalLink, Pencil, Trash2, Loader2, X, Lightbulb, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/tasks/ConfirmDialog";

// Controlled vocabularies — mirror the backend exactly.
const ASPECTS = [
  ["HOOK", "Hook"], ["STORYTELLING", "Storytelling"], ["STRUCTURE", "Structure"],
  ["VISUAL", "Visual"], ["EDITING", "Editing"], ["CAPTION", "Caption"],
  ["CTA", "CTA"], ["TOPIC_ANGLE", "Topic Angle"], ["OTHER", "Other"],
];
const ASPECT_LABEL = Object.fromEntries(ASPECTS);
const PLATFORMS = ["Instagram", "YouTube", "Web", "Other"];

const platformMeta = (p) => {
  switch (p) {
    case "Instagram": return { Icon: Instagram, label: "Instagram" };
    case "YouTube": return { Icon: Youtube, label: "YouTube" };
    case "Web": return { Icon: Globe, label: "Web" };
    default: return { Icon: Link2, label: p || "Link" };
  }
};

const blank = { url: "", platform: "", creator_account: "", title: "", aspects: [], hook_note: "", user_note: "" };

// Small viewport hook so aspect chips cap at 2 on mobile, 3 on tablet/desktop.
function useMaxChips() {
  const [max, setMax] = useState(typeof window !== "undefined" && window.innerWidth < 640 ? 2 : 3);
  useEffect(() => {
    const on = () => setMax(window.innerWidth < 640 ? 2 : 3);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return max;
}

// Inline Inspiration & References section rendered inside the read-only Topic View.
// canEdit gates all add/edit/remove controls; view-only users may still open links.
export function TopicInspirations({ topicId, canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);       // {mode:'create'} or {mode:'edit', item}
  const [removing, setRemoving] = useState(null);
  const enriching = useRef(new Set());

  // Fetch preview metadata for any 'pending' items (once), then patch that card.
  const enrichPending = useCallback((list) => {
    list.filter((i) => i.preview_status === "pending" && !enriching.current.has(i.id)).forEach((i) => {
      enriching.current.add(i.id);
      apiFetch(`/topics/${topicId}/inspirations/${i.id}/preview`, { method: "POST" })
        .then((upd) => setItems((prev) => prev.map((x) => (x.id === upd.id ? upd : x))))
        .catch(() => {})
        .finally(() => enriching.current.delete(i.id));
    });
  }, [topicId]);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/topics/${topicId}/inspirations`)
      .then((rows) => { setItems(rows); enrichPending(rows); })
      .catch((e) => toast.error("Could not load inspiration", { description: e.message }))
      .finally(() => setLoading(false));
  }, [topicId, enrichPending]);
  useEffect(() => { load(); }, [load]);

  const onSaved = (rec) => {
    setForm(null);
    load();
  };

  const refreshOne = async (id) => {
    try {
      const upd = await apiFetch(`/topics/${topicId}/inspirations/${id}/preview?force=true`, { method: "POST" });
      setItems((prev) => prev.map((x) => (x.id === upd.id ? upd : x)));
      toast.success("Preview refreshed");
    } catch (e) { toast.error(e.message); }
  };

  const doRemove = async () => {
    const iid = removing.id;
    try {
      await apiFetch(`/topics/${topicId}/inspirations/${iid}`, { method: "DELETE" });
      toast.success("Inspiration removed");
      setRemoving(null); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid="topic-inspirations">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex min-w-0 items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Lightbulb className="h-4 w-4 shrink-0" /> Inspiration &amp; References
        </h2>
        {canEdit && !form && (
          <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1.5" onClick={() => setForm({ mode: "create" })} data-testid="inspiration-add">
            <Plus className="h-3.5 w-3.5" /> Add Inspiration
          </Button>
        )}
      </div>

      {form && (
        <InspirationForm topicId={topicId} initial={form.mode === "edit" ? form.item : blank}
          itemId={form.mode === "edit" ? form.item.id : null}
          onCancel={() => setForm(null)} onSaved={onSaved} />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-accent" /></div>
      ) : items.length === 0 && !form ? (
        <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 px-4 py-4" data-testid="inspiration-empty">
          <p className="text-sm text-muted-foreground">
            {canEdit ? "Save reels, videos or links that inspire this idea — a great hook, structure or angle." : "No inspiration saved for this topic yet."}
          </p>
          {canEdit && (
            <Button size="sm" variant="outline" className="mt-3 h-8 gap-1.5" onClick={() => setForm({ mode: "create" })} data-testid="inspiration-add-empty">
              <Plus className="h-3.5 w-3.5" /> Add Inspiration
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-2.5" data-testid="inspiration-list">
          {items.map((i) => (
            <InspirationCard key={i.id} item={i} canEdit={canEdit}
              onEdit={() => setForm({ mode: "edit", item: i })} onRemove={() => setRemoving(i)}
              onRefresh={() => refreshOne(i.id)} />
          ))}
        </div>
      )}

      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} destructive testid="inspiration-remove-dialog"
        title="Remove this inspiration?" description="This creative reference will be detached from the topic. This won't affect the topic's approval state."
        confirmLabel="Remove" onConfirm={doRemove} />
    </div>
  );
}

function Thumbnail({ src }) {
  const [ok, setOk] = useState(true);
  if (!src || !ok) return null;
  return (
    <img src={src} alt="" loading="lazy" onError={() => setOk(false)} data-testid="inspiration-thumb"
      className="h-16 w-24 shrink-0 rounded-lg border border-border/60 object-cover sm:h-20 sm:w-32" />
  );
}

function InspirationCard({ item, canEdit, onEdit, onRemove, onRefresh }) {
  const maxChips = useMaxChips();
  const { Icon, label } = platformMeta(item.platform);
  const aspects = item.aspects || [];
  const shown = aspects.slice(0, maxChips);
  const overflow = aspects.length - shown.length;
  const pending = item.preview_status === "pending";
  const hasImage = item.preview_status === "available" && !!item.preview_image_url;
  // Precedence: manual title → preview title → friendly platform fallback.
  const displayTitle = item.title || item.preview_title || `${label} link`;
  const provider = item.creator_account || item.preview_provider || label;

  return (
    <div className="rounded-xl border border-border/70 bg-muted/20 p-3.5" data-testid={`inspiration-card-${item.id}`}>
      <div className="flex items-start gap-3">
        {hasImage && <Thumbnail src={item.preview_image_url} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground">
                <Icon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{provider}</span>
              </div>
              <p className="mt-1 break-words text-[15px] font-medium text-foreground line-clamp-2" data-testid="inspiration-title">{displayTitle}</p>
            </div>
            {canEdit && (
              <div className="flex shrink-0 items-center gap-0.5">
                <button onClick={onRefresh} data-testid={`inspiration-refresh-${item.id}`} aria-label="Refresh preview" className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><RefreshCw className="h-3.5 w-3.5" /></button>
                <button onClick={onEdit} data-testid={`inspiration-edit-${item.id}`} aria-label="Edit inspiration" className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={onRemove} data-testid={`inspiration-remove-${item.id}`} aria-label="Remove inspiration" className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>

          {aspects.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="inspiration-aspects">
              {shown.map((a) => (
                <span key={a} className="rounded-full bg-accent-subtle px-2 py-0.5 text-[11px] font-medium text-accent">{ASPECT_LABEL[a] || a}</span>
              ))}
              {overflow > 0 && <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground">+{overflow} more</span>}
            </div>
          )}
          {item.hook_note && <p className="mt-2 line-clamp-2 break-words text-sm italic leading-relaxed text-foreground/70">“{item.hook_note}”</p>}
          {item.user_note && <p className="mt-1.5 line-clamp-2 break-words text-sm leading-relaxed text-muted-foreground">{item.user_note}</p>}
          {/* Extracted webpage description sits BELOW the user's own note and stays subdued. */}
          {item.preview_description && !item.title && (
            <p className="mt-1.5 line-clamp-2 break-words text-[13px] leading-relaxed text-muted-foreground/70" data-testid="inspiration-preview-desc">{item.preview_description}</p>
          )}

          <div className="mt-3 flex items-center gap-3">
            <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`inspiration-open-${item.id}`}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-accent hover:underline">
              <ExternalLink className="h-3.5 w-3.5" /> Open Link
            </a>
            {pending && (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground/70" data-testid="inspiration-preview-pending">
                <Loader2 className="h-3 w-3 animate-spin" /> Fetching preview…
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InspirationForm({ topicId, initial, itemId, onCancel, onSaved }) {
  const [f, setF] = useState({ ...blank, ...initial, platform: initial.platform || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleAspect = (code) => setF((p) => ({ ...p, aspects: p.aspects.includes(code) ? p.aspects.filter((a) => a !== code) : [...p.aspects, code] }));
  const isEdit = !!itemId;

  const save = async () => {
    if (!f.url.trim()) return toast.error("A link is required.");
    setSaving(true);
    try {
      const body = {
        url: f.url.trim(), platform: f.platform || "", creator_account: f.creator_account.trim(),
        title: f.title.trim(), aspects: f.aspects, hook_note: f.hook_note.trim(), user_note: f.user_note.trim(),
      };
      let rec;
      if (isEdit) rec = await apiFetch(`/topics/${topicId}/inspirations/${itemId}`, { method: "PATCH", body: JSON.stringify(body) });
      else rec = await apiFetch(`/topics/${topicId}/inspirations`, { method: "POST", body: JSON.stringify(body) });
      toast.success(isEdit ? "Inspiration updated" : "Inspiration added");
      onSaved(rec);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-accent/40 bg-card p-4 shadow-sm" data-testid="inspiration-form">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{isEdit ? "Edit inspiration" : "Add inspiration"}</h3>
        <button onClick={onCancel} aria-label="Cancel" className="rounded-md p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
      </div>
      <div className="space-y-3.5">
        <div>
          <Label className="text-xs">Link <span className="text-destructive">*</span></Label>
          <Input value={f.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" autoFocus data-testid="inspiration-url-input" />
        </div>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Platform <span className="text-muted-foreground/60">(optional)</span></Label>
            <Select value={f.platform || "none"} onValueChange={(v) => set("platform", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="inspiration-platform"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Creator / Account <span className="text-muted-foreground/60">(optional)</span></Label>
            <Input value={f.creator_account} onChange={(e) => set("creator_account", e.target.value)} placeholder="@handle or name" data-testid="inspiration-creator-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Title <span className="text-muted-foreground/60">(optional)</span></Label>
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="What is this?" maxLength={300} data-testid="inspiration-title-input" />
        </div>
        <div>
          <Label className="text-xs">What caught your attention? <span className="text-muted-foreground/60">(optional)</span></Label>
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="inspiration-aspect-picker">
            {ASPECTS.map(([code, label]) => {
              const on = f.aspects.includes(code);
              return (
                <button key={code} type="button" onClick={() => toggleAspect(code)} data-testid={`inspiration-aspect-${code}`}
                  className={cn("rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors",
                    on ? "border-accent bg-accent text-accent-foreground" : "border-border bg-muted/30 text-muted-foreground hover:border-accent/50 hover:text-foreground")}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="text-xs">Hook / excerpt worth noting <span className="text-muted-foreground/60">(optional)</span></Label>
          <Input value={f.hook_note} onChange={(e) => set("hook_note", e.target.value)} placeholder="e.g. “Wait for it…” opening line" data-testid="inspiration-hook-input" />
        </div>
        <div>
          <Label className="text-xs">Your note <span className="text-muted-foreground/60">(optional)</span></Label>
          <Textarea value={f.user_note} onChange={(e) => set("user_note", e.target.value)} placeholder="Why is this useful for this topic?" rows={2} data-testid="inspiration-note-input" />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="inspiration-cancel">Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving || !f.url.trim()} data-testid="inspiration-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save Changes" : "Add Inspiration"}
        </Button>
      </div>
    </div>
  );
}
